/**
 * @module FCCore
 * @description Boot-layer primitives for the FitCheck theme: ShadowBase (declarative shadow DOM),
 * ResizeNotifier, OverflowList overflow-row component, and header-height CSS custom-property helpers.
 * @customElement overflow-row
 */
export class ShadowBase extends HTMLElement {
  connectedCallback() {
    if (this.shadowRoot) return;
    const t = this.querySelector(':scope > template[shadowrootmode="open"]');
    if (!(t instanceof HTMLTemplateElement)) return;
    const sr = this.attachShadow({ mode: 'open' });
    sr?.append(t.content.cloneNode(true));
  }
}

export class ResizeNotifier extends ResizeObserver {
  #init = false;

  constructor(callback) {
    super((entries) => {
      if (!this.#init) {
        this.#init = true;
        return;
      }
      callback(entries, this);
    });
  }

  disconnect() {
    this.#init = false;
    super.disconnect();
  }
}

export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class ReflowEvent extends Event {
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

export class OverflowList extends ShadowBase {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  #sch = false;
  #dim = { w: null, h: null };
  #r;
  #ro = new ResizeNotifier((...a) => this.#onChange(...a));
  #mo = new MutationObserver((...a) => this.#onChange(...a));

  attributeChangedCallback(name, _old, val) {
    if (name !== 'disabled') return;
    val === 'true' ? this.#reset() : this.#reflow();
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer')) {
      const run = () => {
        this.removeAttribute('defer');
        this.#setup();
      };
      const { schedule } = this;
      const ric = typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;
      ric(() => schedule(run));
    } else if (this.shadowRoot) {
      this.#setup();
    } else {
      // Shadow root may not exist yet before DOMContentLoaded
      setTimeout(() => this.#setup(), 0);
    }
  }

  disconnectedCallback() {
    this.#ro.disconnect();
    this.#mo.disconnect();
  }

  get schedule() {
    return typeof FitCheck?.utilities?.scheduler?.schedule === 'function'
      ? FitCheck.utilities.scheduler.schedule
      : (cb) => requestAnimationFrame(() => setTimeout(cb, 0));
  }

  get minimumItems() {
    const v = this.getAttribute('minimum-items');
    return v ? parseInt(v, 10) : null;
  }

  get overflowSlot() {
    return this.#r?.overflowSlot;
  }
  get defaultSlot() {
    return this.#r?.defaultSlot;
  }

  #onChange = (evt) => {
    if (this.#sch) return;

    let w = null,
      h = null,
      resize = false;

    for (const [, entry] of evt.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      resize = true;
      w = Math.round(entry.contentRect.width);
      h = Math.round(entry.contentRect.height);
    }

    if (resize) {
      if (!w || !h || (w === this.#dim.w && h === this.#dim.h)) return;
      this.#dim = { w, h };
    }

    this.#sch = true;
    this.schedule(() => {
      this.#reflow();
      this.#sch = false;
    });
  };

  #moveToDefault() {
    const { defaultSlot, overflowSlot } = this.#r;
    for (const el of overflowSlot.assignedElements()) el.slot = defaultSlot.name;
  }

  #reset() {
    const { list } = this.#r;
    this.#mo.disconnect();
    this.#ro.disconnect();
    this.#moveToDefault();
    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  #checkMinimum(visible) {
    const min = this.minimumItems;
    if (min === null) return;
    const reached = visible.length <= min;
    this.toggleAttribute('minimum-reached', reached);
    this.dispatchEvent(new OverflowMinimumEvent(reached));
  }

  showAll() {
    const { placeholder } = this.#r;
    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  #setup() {
    const { shadowRoot: sr } = this;
    if (!sr) return;

    const defaultSlot = sr.querySelector('slot:not([name])');
    const overflowSlot = sr.querySelector('slot[name="overflow"]');
    const moreSlot = sr.querySelector('slot[name="more"]');
    const overflow = sr.querySelector('[part="overflow"]');
    const list = sr.querySelector('[part="list"]');
    const placeholder = sr.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLElement) ||
      !(placeholder instanceof HTMLElement)
    ) {
      return;
    }

    this.#r = { defaultSlot, overflowSlot, moreSlot, overflow, list, placeholder };

    this.addEventListener('reflow', (e) => this.#reflow(e.detail.lastVisibleElement));
    this.#reflow();
  }

  #reflow = (lastVisible) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#r;

    this.#ro.disconnect();
    this.#mo.disconnect();

    // Lock height to prevent layout shifts
    const h = this.firstElementChild?.getBoundingClientRect().height;
    if (h) list.style.height = `${h}px`;
    list.style.setProperty('overflow', 'hidden');

    this.#moveToDefault();

    const els = defaultSlot.assignedElements();
    moreSlot.hidden = true;
    placeholder.hidden = true;
    placeholder.style.removeProperty('width');

    const rootRect = list.getBoundingClientRect();
    this.#dim = { w: Math.round(rootRect.width), h: Math.round(rootRect.height) };

    const listStyle = getComputedStyle(list);
    const measureInlineOverflow = listStyle.flexWrap === 'nowrap';
    const isRtl = listStyle.direction === 'rtl';
    const getVisible = () =>
      els.filter((el) => {
        const rect = el.getBoundingClientRect();
        if (measureInlineOverflow) {
          return isRtl ? rect.left >= rootRect.left - 1 : rect.right <= rootRect.right + 1;
        }

        return rect.top <= rootRect.top + 1;
      });
    let visible = getVisible();

    if (visible.length !== els.length || lastVisible) {
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${h}px`);
      if (lastVisible) lastVisible.style.setProperty('order', '-1');

      visible = getVisible();

      moreSlot.style.removeProperty('order');
      if (lastVisible) lastVisible.style.removeProperty('order');

      // Ensure lastVisible occupies the final visible position
      if (lastVisible) {
        const idx = visible.indexOf(lastVisible);
        if (idx !== -1) {
          visible.splice(idx, 1);
          visible.push(lastVisible);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowing = els.filter((el) => !visible.includes(el));
    const hasOverflow = overflowing.length > 0;
    const phWidth = overflowing[0]?.clientWidth ?? 0;

    for (const el of els) {
      el.slot = overflowing.includes(el) ? overflowSlot.name : defaultSlot.name;
    }

    moreSlot.hidden = !hasOverflow;
    if (hasOverflow) {
      placeholder.style.width = `${phWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowing.length}`);
    this.style.setProperty('--overflow-count', `${overflowing.length}`);
    list.style.removeProperty('overflow');

    if (hasOverflow) this.#checkMinimum(visible);

    this.#ro.observe(this);
    this.#mo.observe(this, { childList: true });
  };
}

if (!customElements.get('overflow-row')) {
  customElements.define('overflow-row', OverflowList);
}

export function calculateHeaderGroupHeight(
  header = document.querySelector('#fit-site-header'),
  headerGroup = document.querySelector('#fit-header-group'),
) {
  if (!headerGroup || !(headerGroup instanceof HTMLElement)) return 0;

  let total = 0;
  for (const el of headerGroup.children) {
    if (el === header || !(el instanceof HTMLElement)) continue;
    const oh = el.offsetHeight;
    if (isFinite(oh) && oh >= 0) total += oh;
  }

  if (!isFinite(total) || total < 0) return 0;

  const isDesktopTransparent = window.innerWidth >= 750 && header instanceof HTMLElement && header.hasAttribute('transparent');
  const isMobileTransparent = window.innerWidth < 750 && header instanceof HTMLElement && header.hasAttribute('transparent-mobile');

  // Transparent header overlaps the first section; add its rendered height.
  if (header instanceof HTMLElement && (isDesktopTransparent || isMobileTransparent) && header.parentElement?.nextElementSibling) {
    const row = header.querySelector('.header__row');
    const rawHeaderHeight = header.offsetHeight;
    const rowHeight = row instanceof HTMLElement ? row.offsetHeight : 0;
    const hh = rawHeaderHeight > 0 ? rawHeaderHeight : rowHeight;
    if (isFinite(hh) && hh >= 0) return total + hh;
  }

  return total;
}

function updateHeaderHeights() {
  if (!document.body) return;

  const hdr = document.querySelector('site-header');
  if (!(hdr instanceof HTMLElement)) return;

  const row = hdr.querySelector('.header__row');
  const rawHeaderHeight = hdr.offsetHeight;
  const rowHeight = row instanceof HTMLElement ? row.offsetHeight : 0;
  const hh = rawHeaderHeight > 0 ? rawHeaderHeight : rowHeight;
  const gh = calculateHeaderGroupHeight(hdr);
  if (!isFinite(hh) || hh < 0 || !isFinite(gh) || gh < 0) return;

  // Bar mode: header is position:fixed / not in flow, so --header-height stays 0
  if (hdr.dataset.headerStyle === 'bar') {
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--bar-header-height', `${hh}px`);
    document.body.style.setProperty('--header-group-height', `${gh}px`);
    return;
  }

  document.body.style.setProperty('--header-height', `${hh}px`);
  document.body.style.setProperty('--header-group-height', `${gh}px`);
}

function updateTransparentHeaderOffset() {
  if (!document.body) return;

  const hdr = document.querySelector('#fit-site-header');
  const grp = document.querySelector('#fit-header-group');
  const sec = grp?.querySelector('.header-section');

  const isDesktopTransparent = window.innerWidth >= 750 && hdr?.hasAttribute('transparent');
  const isMobileTransparent = window.innerWidth < 750 && hdr?.hasAttribute('transparent-mobile');
  if (!sec || !(isDesktopTransparent || isMobileTransparent)) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  // Offset applies only when the header section has no immediate shopify-section sibling
  const hasNext = sec.nextElementSibling?.classList.contains('shopify-section');
  document.body.style.setProperty('--transparent-header-offset-boolean', hasNext ? '0' : '1');
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

updateAllHeaderCustomProperties();
